/**
 * Guest-aware favourites/wishlist client.
 *
 * Behaviour
 * ─────────
 * • Logged-in user clicks the heart  → existing AJAX flow opens the
 *   multi-list popup served by account/my_wishlist/getWishLists.
 * • Guest user clicks the heart      → product_id is toggled in
 *   localStorage['guest_wishlist'] and a short toast appears in the
 *   bottom-right of the viewport: "Adăugat la favorite. Înregistrează-te
 *   ca să-ți salvezi lista permanent." (Romanian / English depending on
 *   document.documentElement.lang).
 *
 * The visual state of every heart icon on the page is refreshed on load
 * so products already in the guest list show a filled heart. The icon
 * swap is class-based — fa-light → fa-solid + a red colour.
 *
 * Merge on login
 * ──────────────
 * When the user becomes logged in (page loads with window.IS_LOGGED ===
 * true) AND there are items in the guest list, we POST them to
 * account/my_wishlist/mergeGuest. The server adds them to the user's
 * default wishlist (creating one called "Favorite" if none exists) and
 * we clear localStorage on success.
 *
 * Login state is read from window.IS_LOGGED, which is emitted by every
 * theme's common/header.tpl in a one-line <script> tag.
 */

const STORAGE_KEY = 'guest_wishlist';
const ICON_FILLED_CLASSES = ['fa-solid'];
const ICON_EMPTY_CLASSES  = ['fa-light'];
const ICON_FILLED_COLOR   = '#e11d48'; // rose-600 — visible on every theme

const LANG = {
    ro: {
        added:    'Adăugat la favorite.',
        removed:  'Eliminat din favorite.',
        hint:     'Înregistrează-te ca să-ți salvezi lista permanent.',
        cta:      'Înregistrează-te',
        register_url: 'index.php?route=account/register',
    },
    en: {
        added:    'Added to favourites.',
        removed:  'Removed from favourites.',
        hint:     'Sign up to save your list permanently.',
        cta:      'Sign up',
        register_url: 'en/index.php?route=account/register',
    },
};

function lang() {
    const code = (document.documentElement.getAttribute('lang') || 'ro').toLowerCase();
    if (code.startsWith('en')) return LANG.en;
    return LANG.ro;
}

// ── Storage ───────────────────────────────────────────────────────────────

function readGuestList() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(Number).filter(n => Number.isInteger(n) && n > 0) : [];
    } catch (_) {
        return [];
    }
}

function writeGuestList(ids) {
    try {
        const uniq = Array.from(new Set(ids.map(Number).filter(n => Number.isInteger(n) && n > 0)));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(uniq));
    } catch (_) {
        // localStorage disabled / quota exceeded — silently ignore.
    }
}

function inGuestList(pid) {
    return readGuestList().indexOf(Number(pid)) !== -1;
}

function toggleGuest(pid) {
    const id = Number(pid);
    const list = readGuestList();
    const idx  = list.indexOf(id);
    if (idx === -1) { list.push(id); writeGuestList(list); return 'added'; }
    list.splice(idx, 1); writeGuestList(list); return 'removed';
}

function clearGuestList() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

// ── Heart icon state ──────────────────────────────────────────────────────

/**
 * Every wishlist trigger in the catalog renders as
 *   <button onclick="wishlist.getWishLists('<product_id>')" ...>
 *     <i class="fa-light fa-heart ..."></i>
 *   </button>
 * No data-* attributes; we extract the product_id from the inline onclick.
 */
function findHeartIcon(button) {
    return button.querySelector('i.fa-heart');
}

function setHeartState(button, isFilled) {
    const icon = findHeartIcon(button);
    if (!icon) return;
    if (isFilled) {
        ICON_EMPTY_CLASSES.forEach(c => icon.classList.remove(c));
        ICON_FILLED_CLASSES.forEach(c => icon.classList.add(c));
        icon.style.color = ICON_FILLED_COLOR;
    } else {
        ICON_FILLED_CLASSES.forEach(c => icon.classList.remove(c));
        ICON_EMPTY_CLASSES.forEach(c => icon.classList.add(c));
        icon.style.color = '';
    }
}

function refreshAllHearts() {
    const list = readGuestList();
    if (window.IS_LOGGED) {
        // Logged-in users have their wishlist state on the server — we
        // don't try to mirror it client-side here (would need an extra
        // round-trip). Heart stays in default unfilled state; the popup
        // is the source of truth.
        return;
    }
    document.querySelectorAll('button[onclick*="wishlist.getWishLists"]').forEach(btn => {
        const m = (btn.getAttribute('onclick') || '').match(/getWishLists\(\s*['"]?(\d+)/);
        if (!m) return;
        setHeartState(btn, list.indexOf(Number(m[1])) !== -1);
    });
}

// ── Toast ─────────────────────────────────────────────────────────────────

let __toastStylesInjected = false;
function injectToastStyles() {
    if (__toastStylesInjected) return;
    __toastStylesInjected = true;
    const css = `
.sw-toast {
    position: fixed; bottom: 24px; right: 24px; max-width: 360px;
    background: #1f2937; color: #fff;
    padding: 12px 16px; border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,.25);
    font: 500 14px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif;
    z-index: 99999; opacity: 0;
    transform: translateY(20px);
    transition: opacity .25s ease, transform .25s ease;
}
.sw-toast.is-visible { opacity: 1; transform: translateY(0); }
.sw-toast a { color: #93c5fd; text-decoration: underline; margin-left: 6px; }
.sw-toast a:hover { color: #bfdbfe; }
@media (max-width: 480px) {
    .sw-toast { left: 12px; right: 12px; bottom: 12px; max-width: none; }
}`;
    const tag = document.createElement('style');
    tag.textContent = css;
    document.head.appendChild(tag);
}

function showToast(message, ctaHref, ctaText) {
    injectToastStyles();
    const el = document.createElement('div');
    el.className = 'sw-toast';
    el.innerHTML = message + (ctaHref ? ' <a href="' + ctaHref + '">' + ctaText + '</a>' : '');
    document.body.appendChild(el);
    // Two-rAF to make sure the transition triggers (not batched into mount).
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-visible')));
    const dwellMs = ctaHref ? 5000 : 3000;
    setTimeout(() => {
        el.classList.remove('is-visible');
        setTimeout(() => el.remove(), 300);
    }, dwellMs);
}

// ── Click handler entry point ─────────────────────────────────────────────

function onHeartClick(product_id) {
    const pid = Number(product_id);
    if (!pid) return;

    if (window.IS_LOGGED) {
        // Existing flow — popup with the user's named lists.
        customAjaxRequest(
            'index.php?route=account/my_wishlist/getWishLists',
            'post',
            'product_id=' + pid,
            function () { if (window.$) $('#wishlistModal').remove(); }
        );
        return;
    }

    // Guest: toggle in localStorage + toast.
    const result = toggleGuest(pid);
    const L = lang();
    refreshAllHearts();
    refreshFavoritesLink();
    // If we're on the guest favourites page, removing an item should
    // make the card disappear without a full reload. Re-hydrate.
    if (document.querySelector('[data-guest-favorites-list]')) {
        hydrateGuestFavoritesPage();
    }
    if (result === 'added') {
        showToast(L.added + ' ' + L.hint, L.register_url, L.cta);
    } else {
        showToast(L.removed);
    }
}

// ── Merge on login ────────────────────────────────────────────────────────

function mergeGuestListIfLogged() {
    if (!window.IS_LOGGED) return;
    const list = readGuestList();
    if (!list.length) return;

    // Fire-and-forget. On success we clear localStorage so subsequent
    // visits don't re-merge. On failure the list stays for next time.
    const form = new FormData();
    list.forEach(id => form.append('product_ids[]', id));

    fetch('index.php?route=account/my_wishlist/mergeGuest', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
    })
    .then(r => r.json())
    .then(j => {
        if (j && j.success) clearGuestList();
    })
    .catch(() => { /* silent — retry next page load */ });
}

// ── Main-menu "Favourites" link visibility ────────────────────────────────

/**
 * The favourites <li> is rendered in every theme's common/menu.tpl with
 * data-favorites-link and a data-server-count attribute populated by
 * catalog/controller/common/menu.php (count of products in the logged-in
 * customer's wishlist; 0 for guests). The item is display:none by default.
 *
 * Visibility rule: total = guest_list_size + server_count.
 *   total > 0   → show <li>, show count badge with that number.
 *   total === 0 → keep hidden.
 */
function refreshFavoritesLink() {
    const links = document.querySelectorAll('[data-favorites-link]');
    if (!links.length) return;
    const guestCount = readGuestList().length;
    links.forEach(li => {
        const serverCount = parseInt(li.getAttribute('data-server-count') || '0', 10);
        const total = guestCount + serverCount;
        if (total > 0) {
            li.style.display = '';
            const badge = li.querySelector('[data-favorites-count]');
            if (badge) {
                badge.textContent = String(total);
                badge.style.display = '';
            }
        } else {
            li.style.display = 'none';
        }
    });
}

// ── Guest favourites page hydration ───────────────────────────────────────

/**
 * On the /account/my_wishlist guest page, the server renders an empty
 * shell with #guest-favorites-list (and #guest-favorites-banner /
 * #guest-favorites-empty placeholders). This function reads the
 * localStorage ids, POSTs them to the AJAX endpoint, injects the
 * returned HTML, and toggles the banner/empty placeholders.
 *
 * Also called after a heart-click toggle from within the list so a
 * removed item disappears without a full page reload.
 */
function hydrateGuestFavoritesPage() {
    const list = document.querySelector('[data-guest-favorites-list]');
    if (!list) return;                  // not on the guest favourites page
    const endpoint = list.getAttribute('data-endpoint');
    const banner   = document.getElementById('guest-favorites-banner');
    const empty    = document.getElementById('guest-favorites-empty');
    const loading  = document.getElementById('guest-favorites-loading');
    const ids      = readGuestList();

    if (banner) banner.style.display = ids.length ? '' : 'none';

    if (!ids.length) {
        list.innerHTML = '';
        if (empty)   empty.style.display = '';
        if (loading) loading.remove();
        return;
    }

    if (empty)   empty.style.display = 'none';
    if (loading) loading.style.display = '';

    const form = new FormData();
    ids.forEach(id => form.append('product_ids[]', id));

    fetch(endpoint, {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    .then(r => r.json())
    .then(j => {
        if (loading) loading.remove();
        list.innerHTML = j && j.html ? j.html : '';
        // Cards just injected — re-run hearts so they show as filled.
        refreshAllHearts();
    })
    .catch(() => {
        if (loading) loading.remove();
        list.innerHTML = '<div class="col-12 text-center py-24 text-danger">Could not load favourites — please refresh.</div>';
    });
}

// ── Public API ────────────────────────────────────────────────────────────

const wishlist = {
    'getWishLists': onHeartClick,   // keep the legacy name so inline onclicks still resolve
    'isInGuestList': inGuestList,
    'getGuestList': readGuestList,
    'clearGuestList': clearGuestList,
    'refreshAllHearts': refreshAllHearts,
    'refreshFavoritesLink': refreshFavoritesLink,
};

window.wishlist = wishlist;

document.addEventListener('DOMContentLoaded', () => {
    refreshAllHearts();
    refreshFavoritesLink();
    hydrateGuestFavoritesPage();
    mergeGuestListIfLogged();
});
